/**
 * RBAC permission helpers — frontend side of the convention cravada em
 * `docs/RBAC-CONVENTION.md` (Story E5.1.2).
 *
 * Backend grava `Permission(resource, action)` em duas colunas e serializa
 * como `f"{resource}:{action}"` (ver `app/api/v1/endpoints/rbac.py`). O split
 * no frontend é na PRIMEIRA `:` — tudo depois é a action. Match é EXATO
 * (case-sensitive, sem wildcards).
 *
 * Helper PURO: opera sobre `PermissionInput`, sem acoplar a nenhum contexto de
 * auth. Os call-sites (Sidebar, QuickActionsWidget, ...) trazem o conjunto de
 * permissões do user; este módulo só decide o match.
 */

import type { Permission } from "@/types/rbac";

/**
 * Inputs aceitos pelo helper. `Set<string>` é o mais performático (O(1) lookup).
 * `readonly string[]` é o que vem direto da API (`UserPermissionsResponse.permissions`)
 * e o formato de `QuickActionsWidgetProps.permissions`. `Permission[]` é útil
 * quando o caller tem objetos `Permission` completos (raro).
 */
export type PermissionInput = Set<string> | readonly string[] | readonly Permission[];

/**
 * Split na PRIMEIRA `:`. Tudo depois é parte da action — preserva hierarquia
 * `notifications:providers:manage` → `{resource: "notifications", action: "providers:manage"}`.
 *
 * Retorna `null` se input não contém `:` interno (malformed conforme convenção §2.3):
 * sem `:`, `:` na posição 0 (resource vazio) ou `:` na última posição (action vazia).
 */
export function splitPermissionString(perm: string): { resource: string; action: string } | null {
  const idx = perm.indexOf(":");
  if (idx <= 0 || idx === perm.length - 1) return null;
  return {
    resource: perm.slice(0, idx),
    action: perm.slice(idx + 1),
  };
}

/**
 * Concatena `Permission` na string canônica `resource:action`.
 */
export function formatPermission(p: Pick<Permission, "resource" | "action">): string {
  return `${p.resource}:${p.action}`;
}

/**
 * Converte `UserPermissionsResponse.permissions: string[]` em `Set<string>`
 * para lookup O(1). Use após `rbacService.getUserPermissions()`.
 */
export function parsePermissionsFromAPI(apiPermissions: readonly string[]): Set<string> {
  return new Set(apiPermissions);
}

/**
 * Normaliza qualquer `PermissionInput` em `Set<string>` para lookup uniforme.
 * Interno — exportado apenas para uso em testes / hooks especializados.
 *
 * `Set<string>` passa por referência (lookup O(1) — design intencional; o helper
 * só lê, nunca muta). Para arrays, normaliza ELEMENTO A ELEMENTO (não pelo tipo
 * do índice 0) — assim um array misto/sujo (ex.: dado cruzando o boundary da API
 * sem tipagem) nunca silencia um membro: string entra como está, `Permission`
 * vira `resource:action`.
 */
export function toPermissionSet(input: PermissionInput): Set<string> {
  if (input instanceof Set) return input;
  return new Set(
    Array.from(input as readonly (string | Permission)[], (item) =>
      typeof item === "string" ? item : formatPermission(item)
    )
  );
}

/**
 * Match EXATO de uma permission contra o conjunto do usuário.
 * Wildcards NÃO são suportados (ver doc §5).
 *
 * @example
 *   hasPermission("audit:read", new Set(["audit:read", "audit:export"])) // true
 *   hasPermission("audit:admin", ["audit:read"])                          // false
 *   hasPermission("notifications:providers:manage", userPerms)            // true se exato
 */
export function hasPermission(required: string, userPerms: PermissionInput): boolean {
  if (!required) return false;
  const set = toPermissionSet(userPerms);
  return set.has(required);
}

/**
 * True se o usuário tem PELO MENOS UMA das permissions listadas.
 * Lista vazia → false (não há nada para "ter alguma").
 */
export function hasAnyPermission(required: readonly string[], userPerms: PermissionInput): boolean {
  if (required.length === 0) return false;
  const set = toPermissionSet(userPerms);
  return required.some((p) => set.has(p));
}

/**
 * True se o usuário tem TODAS as permissions listadas.
 * Lista vazia → true (vacuously true — nenhuma exigência a falhar).
 */
export function hasAllPermissions(
  required: readonly string[],
  userPerms: PermissionInput
): boolean {
  if (required.length === 0) return true;
  const set = toPermissionSet(userPerms);
  return required.every((p) => set.has(p));
}
